import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';

interface TruncatedTooltipCellProps {
  text?: string | null;
  fallback?: string;
}

const TruncatedTooltipCell: React.FC<TruncatedTooltipCellProps> = ({ text, fallback = '—' }) => {
  const displayText = text?.trim() ? text : fallback;
  const textRef = useRef<HTMLElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = textRef.current;
    if (element) {
      setIsOverflowed(element.scrollWidth > element.clientWidth);
    }
  }, []);

  useLayoutEffect(() => {
    checkOverflow();
    const element = textRef.current;
    if (!element) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [checkOverflow, displayText]);

  const cellContent = (
    <Typography
      ref={textRef}
      variant='body2'
      noWrap
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
        fontSize: 14,
        fontWeight: 400,
        color: '#101828',
      }}
    >
      {displayText}
    </Typography>
  );

  if (!text?.trim()) {
    return cellContent;
  }

  return (
    <Tooltip
      title={text}
      arrow
      placement='bottom-start'
      enterTouchDelay={0}
      disableHoverListener={!isOverflowed}
    >
      {cellContent}
    </Tooltip>
  );
};

export default TruncatedTooltipCell;
